import React, { useEffect, useState } from "react";
import Icon from "@mdi/react";
import { mdiMapMarker, mdiCheckCircle } from "@mdi/js";
import avatarPlaceholder from "../assets/avatar-placeholder.png";
import defaultCover from "../assets/instructor_default_cover.jpg";
import { getDanceStyleLabel } from "../config/danceStyles";
import "./InstructorCard.css";

// Distances this far or more aren't shown (a Brisbane instructor seen from London).
const SHOW_DISTANCE_BELOW_KM = 1000;

/**
 * Resolve the cover background to a URL that is known to load.
 *
 * The cover is rendered as a CSS background-image, which has no onError hook —
 * so a broken-but-truthy URL (e.g. S3 erroring) would otherwise render blank
 * with no fallback. We preload the candidate URL via an Image() and only use
 * it once it actually loads; on error (or when missing) we use the bundled
 * default cover. Mirrors the avatar's <img onError> resilience.
 */
const useCoverImage = coverImageUrl => {
    const [resolvedCover, setResolvedCover] = useState(defaultCover);

    useEffect(() => {
        if (!coverImageUrl) {
            setResolvedCover(defaultCover);
            return;
        }

        let cancelled = false;
        const img = new Image();
        img.onload = () => { if (!cancelled) setResolvedCover(coverImageUrl); };
        img.onerror = () => { if (!cancelled) setResolvedCover(defaultCover); };
        img.src = coverImageUrl;

        return () => { cancelled = true; };
    }, [coverImageUrl]);

    return resolvedCover;
};

/**
 * InstructorCard — shared instructor card component
 *
 * Used in:
 * - InstructorSearch.js (search results grid)
 * - InstructorProfilePage.js (live student-facing card preview)
 *
 * Props:
 * - instructor: object with card data (see shape below)
 * - onClick: optional click handler
 *
 * Instructor shape:
 * {
 *   name, title, profileImageUrl, coverImageUrl,
 *   location (string | { description }),
 *   danceStyles [], bio, teachingFormats [], isVerified
 * }
 */
const InstructorCard = ({ instructor, onClick }) => {
    const coverImage = useCoverImage(instructor.coverImageUrl);

    return (
        <div className="instructor-card" onClick={onClick}>
            <div
                className="card-header"
                style={{ backgroundImage: `url('${coverImage}')` }}
            >
                <div className="avatar-ring">
                    <img
                        src={instructor.profileImageUrl || avatarPlaceholder}
                        alt={instructor.name || instructor.title}
                        className="instructor-avatar"
                        onError={e => {
                            e.target.src = avatarPlaceholder;
                        }}
                    />
                </div>
                {instructor.isVerified && (
                    <div className="verified-badge">
                        <Icon path={mdiCheckCircle} size={0.8} color="white" />
                    </div>
                )}
            </div>

            <div className="card-body">
                <h3>{instructor.name || instructor.title}</h3>

                {instructor.location && (
                    <p className="instructor-location">
                        <Icon path={mdiMapMarker} size={0.7} />
                        {typeof instructor.location === "object"
                            ? instructor.location.description
                            : instructor.location}
                        {/* Nearest-first searches only; same rule as the app card. */}
                        {instructor.distanceKm !== undefined && instructor.distanceKm < SHOW_DISTANCE_BELOW_KM && (
                            <span className="instructor-distance"> · {Math.max(1, instructor.distanceKm)} km</span>
                        )}
                    </p>
                )}

                {instructor.danceStyles?.length > 0 && (
                    <div className="dance-styles">
                        {instructor.danceStyles.slice(0, 3).map((style, i) => (
                            <span key={i} className="style-tag">
                                {getDanceStyleLabel(style)}
                            </span>
                        ))}
                        {instructor.danceStyles.length > 3 && (
                            <span className="style-tag more">+{instructor.danceStyles.length - 3}</span>
                        )}
                    </div>
                )}

                {instructor.bio && <p className="instructor-bio">{instructor.bio}</p>}

                {instructor.teachingFormats?.length > 0 && (
                    <div className="teaching-formats">
                        {instructor.teachingFormats.map((f, i) => (
                            <span key={i} className="format-badge">
                                {f}
                            </span>
                        ))}
                    </div>
                )}
            </div>

            <div className="card-footer">
                <span className="view-profile-link">View Profile →</span>
            </div>
        </div>
    );
};

export default InstructorCard;
